import { PM_REACT_SEARCH_STYLES } from "./styles/pmReactStyles";

import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { PmReactSearchApp, type PmReactSearchProps } from "./PmReactSearchApp";

type PmReactSearchApi = {
  version: string;
  mount: (el: Element, props: PmReactSearchProps) => void;
  update: (el: Element, props: PmReactSearchProps) => void;
  unmount: (el: Element) => void;
  // The host calls this when its page is being left (another tab, the
  // messenger, login); the flow decides whether that is worth reporting.
  leave: (el: Element) => void;
};

type ShadowMount = {
  root: Root;
  shadowRoot: ShadowRoot;
  container: HTMLDivElement;
  onLeave: (() => void) | null;
  registerLeave: (handler: (() => void) | null) => void;
};

const mounts = new WeakMap<Element, ShadowMount>();

/**
 * Create a Shadow DOM boundary for complete style isolation.
 * This prevents Ionic/Angular global styles from affecting React content.
 */
const createShadowMount = (el: Element): ShadowMount => {
  const anyEl = el as any;
  const shadowRoot: ShadowRoot =
    (el as Element & { shadowRoot?: ShadowRoot }).shadowRoot ||
    (typeof anyEl.attachShadow === "function"
      ? anyEl.attachShadow({ mode: "open" })
      : (() => {
          throw new Error(
            "Shadow DOM is not supported by this browser. PmReact.search requires Shadow DOM for style isolation.",
          );
        })());

  // If the element already had a shadowRoot (e.g., mount → unmount → mount),
  // clear it so we don't accumulate duplicate style/container nodes.
  while (shadowRoot.firstChild) shadowRoot.removeChild(shadowRoot.firstChild);

  // Inject our scoped CSS into the shadow root
  const styleEl = document.createElement("style");
  styleEl.textContent = PM_REACT_SEARCH_STYLES;
  shadowRoot.appendChild(styleEl);

  // Create a container for React inside the shadow root
  const container = document.createElement("div");
  container.className = "pm-react-shadow-container";
  container.style.height = "100%";
  container.style.width = "100%";
  shadowRoot.appendChild(container);

  // Create React root inside shadow DOM
  const root = createRoot(container);

  const mount: ShadowMount = {
    root,
    shadowRoot,
    container,
    onLeave: null,
    registerLeave: (handler) => {
      mount.onLeave = handler;
    },
  };
  return mount;
};

const render = (el: Element, props: PmReactSearchProps) => {
  let mount = mounts.get(el);

  if (!mount) {
    mount = createShadowMount(el);
    mounts.set(el, mount);
  }

  mount.root.render(<PmReactSearchApp {...props} registerLeave={mount.registerLeave} />);
};

const leave = (el: Element) => {
  const mount = mounts.get(el);
  if (mount && mount.onLeave) mount.onLeave();
};

const unmount = (el: Element) => {
  const mount = mounts.get(el);
  if (!mount) return;
  mount.root.unmount();
  mounts.delete(el);

  // Optional cleanup: remove injected nodes to keep DOM tidy.
  // Note: the ShadowRoot remains attached; it cannot be detached.
  const shadowRoot = (el as any).shadowRoot as ShadowRoot | undefined;
  if (shadowRoot) shadowRoot.innerHTML = "";
};

const api: PmReactSearchApi = {
  version: "0.0.0",
  mount: render,
  update: render,
  unmount,
  leave,
};

const w = typeof window !== "undefined" ? (window as any) : null;
if (w) {
  w.PmReact = w.PmReact || {};
  w.PmReact.search = api;
}
